/**
 * How it works (`/how-it-works` with SLOT_MODEL on; pages/HowItWorks.jsx stays for flag-off).
 *
 * Sept 2026 redesign: ports site/how-it-works/index.html of the approved package (centred
 * intro, the three role tabs with four steps each, the "It starts at a table" call-out)
 * onto components/site. The tabs keep the page's deep links: /how-it-works#patrons
 * (guests), #businesses and #venues select that path (howItWorksParts.jsx RoleTabs); the
 * package's #guests and any upper-case spelling work too (the address is made canonical).
 *
 * Copy is the package's, made exact where the product is (copy audit,
 * investigations/04-copy-audit.md): guests claim with just an email and get the code on
 * screen plus an email with a link back to their codes; one paid advertiser per category
 * at each venue; artwork joins the next quarterly print run; the monthly report shows
 * recorded activity and keeps estimates separate; venues get the coasters free (local
 * advertisers pay) and quantities and timing are confirmed with each venue. No figures on
 * this page: prices and coaster quantities live on /pricing.
 */
import React from 'react';
import SEO from '../../components/SEO';
import { Button, CtaBand, PageHero, SiteContainer, SiteImage } from '@/components/site';
import { DemoOfferButton } from '@/components/site/demo';
import { EnquiryButton } from '@/components/site/enquiry';
import { AudienceMain, PAGE_BUTTON } from './audienceParts';
import { JourneySteps, RoleTabs } from './howItWorksParts';

// The title names the brand, so <SEO> (composeTitle) does not append " | Tappd" again: the
// document title is "How Tappd works", the package's title.
export const HOW_IT_WORKS_SEO = {
  title: 'How Tappd works',
  description:
    'How Tappd works for guests, businesses and venues: scan a coaster for local deals, advertise with one slot per category per venue, or host free coasters.',
  url: '/how-it-works',
};

export const PATRON_STEPS = [
  { photo: 'HIW-G-01', title: 'Scan the coaster', text: 'Open your phone camera and scan the QR code at your table.' },
  {
    photo: 'HIW-G-02',
    title: 'Find something nearby',
    text: 'Browse offers from local businesses featured at that venue. No app or account needed.',
  },
  {
    photo: 'HIW-G-03',
    title: 'Claim with your email',
    text: 'Choose an offer, read its terms, and enter your email. Your code shows on screen, and we email it to you with a link back to your codes.',
  },
  { photo: 'HIW-G-04', title: 'Show up & enjoy', text: 'Visit the business and show your code according to the offer terms.' },
];

export const BUSINESS_STEPS = [
  {
    photo: 'HIW-B-01',
    title: 'Start with a listing',
    text: 'Introduce your business and one offer. A free listing needs no payment card.',
  },
  {
    photo: 'HIW-B-02',
    title: 'Find your venues',
    text: 'Choose your area and a paid tier. Each venue has one paid advertiser per category, so check availability first.',
  },
  {
    photo: 'HIW-B-03',
    title: 'Reach the table',
    text: 'Your artwork joins the next quarterly print run. Your digital offer can be updated separately.',
  },
  {
    photo: 'HIW-B-04',
    title: 'Follow the activity',
    text: 'Your monthly report shows recorded placements, scans, claims, leads, and redemptions. Estimates are kept separate.',
  },
];

export const VENUE_STEPS = [
  {
    photo: 'HIW-V-01',
    title: 'Tell us about your venue',
    text: 'Share a few details, including location and approximate guest numbers.',
  },
  {
    photo: 'HIW-V-02',
    title: 'Agree the plan',
    text: 'We’ll confirm eligibility, local advertising fit, quantities, and timing.',
  },
  {
    photo: 'HIW-V-03',
    title: 'Receive your coasters',
    text: 'They’re free for your venue: local advertisers pay for them, and Tappd handles production and delivery.',
  },
  {
    photo: 'HIW-V-04',
    title: 'Bring them to the table',
    text: 'Use your coasters as usual. Guests discover the businesses nearby.',
  },
];

/** The three paths: tab id = the deep-link fragment (#patrons, #businesses, #venues). */
export const HOW_IT_WORKS_PATHS = [
  { id: 'patrons', aliases: ['guests'], label: 'For guests', steps: PATRON_STEPS },
  { id: 'businesses', label: 'For businesses', steps: BUSINESS_STEPS },
  { id: 'venues', label: 'For venues', steps: VENUE_STEPS },
];

// Each path's actions, centred under its steps (stacked full width below 481px), in the
// CTA vocabulary of the upgrade brief (§2.2, §3.2): one label per intent, sitewide.
const ACTIONS = {
  patrons: (
    <>
      <Button className={PAGE_BUTTON} to="/local-deals-near-me">Find deals near me</Button>
      <DemoOfferButton className={PAGE_BUTTON} variant="secondary">Try an example offer</DemoOfferButton>
    </>
  ),
  businesses: (
    <>
      <EnquiryButton className={PAGE_BUTTON} role="advertiser">Check availability</EnquiryButton>
      <Button className={PAGE_BUTTON} to="/free-listing" variant="secondary">Start with a free listing</Button>
    </>
  ),
  venues: (
    <>
      <Button className={PAGE_BUTTON} to="/venue/apply">Get free coasters</Button>
      <EnquiryButton className={PAGE_BUTTON} role="venue" variant="secondary">Talk to the team</EnquiryButton>
    </>
  ),
};

const ROLES = HOW_IT_WORKS_PATHS.map(({ id, aliases, label, steps }) => ({
  id,
  aliases,
  label,
  content: (
    <>
      <JourneySteps steps={steps} label={label} />
      <div className="mt-[45px] flex flex-col items-stretch gap-3.5 min-[481px]:flex-row min-[481px]:flex-wrap min-[481px]:items-center min-[481px]:justify-center">
        {ACTIONS[id]}
      </div>
    </>
  ),
}));

export default function HowItWorksSlot() {
  return (
    <AudienceMain>
      <SEO title={HOW_IT_WORKS_SEO.title} description={HOW_IT_WORKS_SEO.description} url={HOW_IT_WORKS_SEO.url} image="/images/og/how-it-works.jpg" />

      <PageHero
        align="center"
        eyebrow="One coaster. Three connections."
        title={(
          <>
            A little curiosity.
            {' '}
            <br />
            A closer neighborhood.
          </>
        )}
        lede="Guests find local offers. Businesses get discovered. Venues get sponsored coasters. Here’s how your part works."
      />

      {/* Upgrade brief §3.2: the wide image band under the intro (21:9 desktop, 4:5 phones). */}
      <SiteContainer className="mb-10 nav:mb-12">
        <SiteImage
          id="HIW-HERO-01"
          ratio="4x5"
          navRatio="21x9"
          priority
          sizes="(min-width: 1808px) 1651px, 100vw"
          className="border border-brand-rule"
          testId="hiw-hero-photo"
        />
      </SiteContainer>

      <SiteContainer>
        <RoleTabs label="Choose your path" roles={ROLES} />
      </SiteContainer>

      <CtaBand
        tone="peach"
        eyebrow="There’s a place for you here"
        title="It starts at a table."
        text="And it can lead to your next favorite local business."
        actions={<Button className={PAGE_BUTTON} to="/local-deals-near-me">Find deals near me</Button>}
      />
    </AudienceMain>
  );
}
